<template>
  <div class="rsm-inoculation-overlay">
    <iframe
      ref="iframe"
      class="inoc-iframe"
      :src="vimeoSrc"
      frameborder="0"
      allow="autoplay; fullscreen"
      allowfullscreen
    ></iframe>

    <!-- Tap-to-start gate: iOS Safari blocks unmuted autoplay audio unless play/unmute
         happens inside a direct user gesture (same root cause as the feed's mute-on-autoplay
         issue). The iframe loads paused+muted; this tap starts playback with sound. -->
    <div v-if="!started" class="inoc-start-gate" @click="start">
      <div class="inoc-start-gate__icon">
        <svg viewBox="0 0 24 24" fill="white" width="64" height="64"><path d="M8 5v14l11-7z"/></svg>
      </div>
      <div class="inoc-start-gate__label">Appuie pour lancer la vidéo</div>
    </div>

    <!-- Tap target: toggles play/pause -->
    <div v-else class="inoc-tap" @click="togglePlayPause"></div>

    <!-- Pause indicator -->
    <transition name="inoc-fade">
      <div v-if="videoPaused" class="inoc-pause-icon">
        <svg viewBox="0 0 24 24" fill="white" width="64" height="64"><path d="M8 5v14l11-7z"/></svg>
      </div>
    </transition>

    <!-- Subtle progress bar at bottom -->
    <div class="inoc-progress">
      <div class="inoc-progress__fill" :style="{ width: (progress * 100) + '%' }"></div>
    </div>

    <!-- Continue button — appears once video finishes -->
    <button v-if="ready" class="inoc-continue-btn" @click="onContinue">
      Continuer →
    </button>
  </div>
</template>

<script>
const VIMEO = {
  boys:  { id: '1207800117', h: '9970054781' },
  // Re-uploaded 2026-07-29 to test whether the original was a broken transcode (silent audio
  // on iPhone Safari only). The share link given for this upload had no private hash (h=) --
  // if the video's privacy needs one to embed, this will fail to load entirely. If so, get the
  // link from Vimeo's "Embed" tab specifically (not the general "Share" link), which includes it.
  girls: { id: '1213878314', h: null },
};

export default {
  props: {
    feedType: { type: String, default: 'general' },
  },
  data() {
    return {
      ready: false,
      progress: 0,
      videoPaused: false,
      started: false,
      playerReady: false,
      _pendingStart: false,
    };
  },
  computed: {
    vimeoSrc() {
      const entry = VIMEO[this.feedType === 'female' ? 'girls' : 'boys'];
      // No autoplay: playback starts from the tap-to-start gate so the play call happens
      // inside a direct user gesture (required for sound on iOS Safari).
      // muted=0 requests an unmuted player on init; kept as a first line of defence, but NOT
      // relied on alone (confirmed insufficient on its own) -- see the explicit setVolume/unmute
      // calls below, which are the actual mechanism now.
      // quality=1080p forces a specific rendition instead of Vimeo's adaptive selection --
      // an experiment for the female-video-silent-on-iPhone-Safari-only bug, in case a lower
      // bitrate rendition (more likely to be picked on mobile networks) has a broken audio
      // track for that specific upload. Requires a Vimeo plan that honours this param; if not,
      // it's silently ignored and behaviour is unchanged.
      const hashParam = entry.h ? `h=${entry.h}&` : '';
      return `https://player.vimeo.com/video/${entry.id}?${hashParam}autoplay=0&muted=0&quality=1080p&controls=0&api=1`;
    },
  },
  mounted() {
    window.addEventListener('message', this._onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._completeTimer);
  },
  methods: {
    start() {
      this.started = true;
      if (this.playerReady) {
        this._unmuteAndPlay();
      } else {
        // The iframe's postMessage bridge hasn't signalled 'ready' yet -- sending commands now
        // could be silently dropped. Defer to the 'ready' handler below instead of guessing.
        this._pendingStart = true;
      }
    },
    // Sent directly inside the click handler (a direct user gesture) so iOS Safari allows
    // audio. Sends both setVolume and unmute -- two different Vimeo Player.js mechanisms for
    // the same intent, since muted=0 in the URL alone was confirmed NOT sufficient (video played
    // silently on the female inoculation video even with that param set).
    _unmuteAndPlay() {
      this._sendCmd('setVolume', 1);
      this._sendCmd('setMuted', false);
      this._sendCmd('play');
      console.log('[Inoculation] unmute+play sent', this.feedType, this.vimeoSrc);
    },
    togglePlayPause() {
      if (this.videoPaused) {
        this._sendCmd('play');
        this.videoPaused = false;
      } else {
        this._sendCmd('pause');
        this.videoPaused = true;
      }
    },
    _sendCmd(method, value) {
      const iframe = this.$refs.iframe;
      if (!iframe) return;
      const msg = value !== undefined ? { method, value } : { method };
      iframe.contentWindow.postMessage(JSON.stringify(msg), '*');
    },
    _onMessage(e) {
      // Ignore messages from any other window (e.g. the feed's YouTube iframe, which is
      // still present behind this overlay) -- only react to this component's own iframe.
      const iframe = this.$refs.iframe;
      if (!iframe || e.source !== iframe.contentWindow) return;

      let data;
      try { data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch (err) { return; }
      if (!data || !data.event) return;

      // Log every event/error from this specific video so a real-device test (e.g. via Safari's
      // remote Web Inspector: Mac Safari > Develop > [iPhone] > page) shows exactly what Vimeo
      // reports back, instead of us guessing at postMessage commands blind.
      console.log('[Inoculation RECV]', this.feedType, data.event, JSON.stringify(data.data !== undefined ? data.data : ''));

      if (data.event === 'ready') {
        this.playerReady = true;
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'finish' }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'playProgress' }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'volumechange' }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'error' }), '*');
        if (this._pendingStart) {
          this._pendingStart = false;
          this._unmuteAndPlay();
        }
      }
      if (data.event === 'playProgress' && data.data) {
        this.progress = data.data.percent || 0;
      }
      if (data.event === 'finish') {
        this.progress = 1;
        this.ready = true;
      }
      if (data.event === 'error') {
        console.error('[Inoculation] Vimeo player error', this.feedType, data.data);
      }
    },
    onContinue() {
      this._completeTimer = setTimeout(() => this.$emit('inoculation-complete'), 800);
    },
  },
};
</script>

<style lang="scss" scoped>
.rsm-inoculation-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #000;
}

.inoc-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.inoc-tap {
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
}

.inoc-start-gate {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.55);

  &__icon {
    width: 88px;
    height: 88px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__label {
    color: #fff;
    font-size: 15px;
    font-weight: 600;
  }
}

.inoc-pause-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.inoc-progress {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(100%, calc(100vh * 9 / 16));
  height: 5px;
  background: rgba(255, 255, 255, 0.35);
  z-index: 2;

  &__fill {
    height: 100%;
    background: #fff;
    transition: width 0.5s linear;
  }
}

.inoc-continue-btn {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  background: #fff;
  color: #000;
  border: none;
  border-radius: 28px;
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  animation: fadeUp 0.4s ease;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateX(-50%) translateY(12px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.inoc-fade-enter-active,
.inoc-fade-leave-active { transition: opacity 0.2s; }
.inoc-fade-enter,
.inoc-fade-leave-to    { opacity: 0; }
</style>
